/**
 * FinancialsView - NVIDIA Financials > Key Stats page view
 *
 * Displays financial key statistics including revenue, EBITDA, EBIT, and valuation multiples
 */

import { BaseView } from './BaseView';
import { DownloadService } from '../services/DownloadService';
import { StorageService } from '../services/StorageService';

export class FinancialsView extends BaseView {
	private downloadService: DownloadService;
	private storageService: StorageService;

	constructor() {
		super();
		this.downloadService = new DownloadService();
		this.storageService = new StorageService();
	}
	/**
	 * Render the financials view content
	 */
	render(): string {
		return `
			<div class="company-header">
				<div class="company-name"><a href="/profile">NVIDIA Corporation (NasdaqGS:NVDA)</a> Financials &gt; Key Stats</div>
			</div>
			<div class="button-row">
				<div class="tool-button download-button">
					<img src="../img/icon1.png" alt="Download Financials" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Download Financials</span>
				</div>
				<div class="tool-button pdf-button" data-pdf="../files/NVIDIA-10-K-08-27-2025.pdf">
					<img src="../img/icon3.png" alt="10-K" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">10-K</span>
				</div>
				<div class="tool-button pdf-button" data-pdf="../files/NVIDIA-10-Q-08-27-2025.pdf">
					<img src="../img/icon4.png" alt="10-Q" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">10-Q</span>
				</div>
			</div>

			<div class="tabs-container">
				<div class="tab-group">
					<a href="/financials" class="tab active">Key Stats</a>
					<a href="/income-statement" class="tab">Income Statement</a>
					<a href="/balance-sheet" class="tab">Balance Sheet</a>
					<a href="/cash-flow" class="tab">Cash Flow</a>
				</div>
			</div>

			<div class="financials-controls">
				<div class="control-row">
					<div class="control-group">
						<span class="control-label">Template:</span>
						<select class="control-select">
							<option selected>Standard</option>
						</select>
					</div>
					<div class="control-group">
						<span class="control-label">Restatement:</span>
						<select class="control-select">
							<option selected>Latest Filings</option>
						</select>
					</div>
				</div>
				<div class="control-row">
					<div class="control-group">
						<span class="control-label">Period Type:</span>
						<select class="control-select">
							<option selected>Annual</option>
							<option>Quarterly</option>
							<option>Semi-Annual</option>
							<option>LTM</option>
						</select>
					</div>
					<div class="control-group">
						<span class="control-label">Order:</span>
						<select class="control-select">
							<option selected>Latest on Right</option>
						</select>
					</div>
					<button class="go-button">Go</button>
				</div>
			</div>
			<div class="timeline-wrapper">
				<div class="timeline-container">
					<div class="timeline-nav-left">
						<button type="button" class="timeline-arrow">◀</button>
					</div>
					<div class="timeline-track">
						<div class="year-ticks">
							<div class="tick" data-year="'99"></div>
							<div class="tick" data-year="'01"></div>
							<div class="tick" data-year="'03"></div>
							<div class="tick" data-year="'05"></div>
							<div class="tick" data-year="'07"></div>
							<div class="tick" data-year="'09"></div>
							<div class="tick" data-year="'11"></div>
							<div class="tick" data-year="'13"></div>
							<div class="tick" data-year="'15"></div>
							<div class="tick" data-year="'17"></div>
							<div class="tick" data-year="'19"></div>
							<div class="tick" data-year="'21"></div>
							<div class="tick" data-year="'23"></div>
							<div class="tick" data-year="'25"></div>
						</div>
						<div class="selection-area">
							<div class="handle left"></div>
							<div class="selected-range"></div>
							<div class="handle right"></div>
						</div>
					</div>
					<div class="timeline-nav-right">
						<button type="button" class="timeline-arrow">▶</button>
						<button type="button" class="view-all-button">View All</button>
						<button type="button" class="update-range-button">Update Year Range</button>
					</div>
				</div>
			</div>

			<div class="main-section">
				<!-- Pre-rendered "No data" message - hidden by default, shown by strategies when needed -->
				<div class="no-data-message" style="display: none;">
					<div style="display: flex; align-items: center; justify-content: center; min-height: 400px; width: 100%;">
						<p style="font-size: 16px; color: #666; text-align: center; font-family: 'Times New Roman', Times, serif;">
							No data available for this timeline range.
						</p>
					</div>
				</div>

				<div class="key-financials-wrapper">
					<div><strong>Key Financials<sup>1</sup></strong> <em>In Millions of the trading currency, except per share items.</em></div>
				</div>

				<div class="financials-table-container" style="width:95%;">
					<table class="financials-table">
						<thead>
							<tr>
								<th scope="col" class="label-column">For the Fiscal Period Ending</th>
								<th scope="col">Jan-26-2020A</th>
								<th scope="col">Jan-31-2021A</th>
								<th scope="col">Jan-30-2022A</th>
								<th scope="col">Jan-29-2023A</th>
								<th scope="col">Jan-28-2024A</th>
								<th scope="col">Jan-26-2025A</th>
								<th scope="col">Jan-31-2026E</th>
							</tr>
						</thead>
						<tbody>
							<tr>
								<td class="label-column">Total Revenue<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em;">Growth Over Prior Year</span></td>
								<td class="number-cell">10,918.0<br><span style="margin-left: 1.5em; color: #555;"><em>(6.8%)</em></span></td>
								<td class="number-cell">16,675.0<br><span style="margin-left: 1.5em; color: #555;"><em>52.7%</em></span></td>
								<td class="number-cell">26,914.0<br><span style="margin-left: 1.5em; color: #555;"><em>61.4%</em></span></td>
								<td class="number-cell">26,974.0<br><span style="margin-left: 1.5em; color: #555;"><em>0.2%</em></span></td>
								<td class="number-cell">60,922.0<br><span style="margin-left: 1.5em; color: #555;"><em>125.9%</em></span></td>
								<td class="number-cell">130,497.0<br><span style="margin-left: 1.5em; color: #555;"><em>114.2%</em></span></td>
								<td class="number-cell">199,809.65<br><span style="margin-left: 1.5em; color: #555;"><em>54.55%</em></span></td>
							</tr>
							<tr>
								<td class="label-column">Gross Profit<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em;">Margin %</span></td>
								<td class="number-cell">6,768.0<br><span style="margin-left: 1.5em; color: #555;"><em>62.0%</em></span></td>
								<td class="number-cell">10,557.0<br><span style="margin-left: 1.5em; color: #555;"><em>63.3%</em></span></td>
								<td class="number-cell">17,475.0<br><span style="margin-left: 1.5em; color: #555;"><em>64.9%</em></span></td>
								<td class="number-cell">15,356.0<br><span style="margin-left: 1.5em; color: #555;"><em>56.9%</em></span></td>
								<td class="number-cell">44,301.0<br><span style="margin-left: 1.5em; color: #555;"><em>72.7%</em></span></td>
								<td class="number-cell">97,858.0<br><span style="margin-left: 1.5em; color: #555;"><em>75.0%</em></span></td>
								<td class="number-cell">-<br><span style="margin-left: 1.5em; color: #555;"><em>70.91%</em></span></td>
							</tr>
							<tr>
								<td class="label-column">EBITDA<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em;">Margin %</span></td>
								<td class="number-cell">3,227.0<br><span style="margin-left: 1.5em; color: #555;"><em>29.6%</em></span></td>
								<td class="number-cell">5,819.0<br><span style="margin-left: 1.5em; color: #555;"><em>34.9%</em></span></td>
								<td class="number-cell">11,215.0<br><span style="margin-left: 1.5em; color: #555;"><em>41.7%</em></span></td>
								<td class="number-cell">7,121.0<br><span style="margin-left: 1.5em; color: #555;"><em>26.4%</em></span></td>
								<td class="number-cell">34,480.0<br><span style="margin-left: 1.5em; color: #555;"><em>56.6%</em></span></td>
								<td class="number-cell">83,317.0<br><span style="margin-left: 1.5em; color: #555;"><em>63.8%</em></span></td>
								<td class="number-cell">118,236.88<br><span style="margin-left: 1.5em; color: #555;"><em>59.17%</em></span></td>
							</tr>
							<tr>
								<td class="label-column">EBIT<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em;">Margin %</span></td>
								<td class="number-cell">2,846.0<br><span style="margin-left: 1.5em; color: #555;"><em>26.1%</em></span></td>
								<td class="number-cell">4,721.0<br><span style="margin-left: 1.5em; color: #555;"><em>28.3%</em></span></td>
								<td class="number-cell">10,041.0<br><span style="margin-left: 1.5em; color: #555;"><em>37.3%</em></span></td>
								<td class="number-cell">5,577.0<br><span style="margin-left: 1.5em; color: #555;"><em>20.7%</em></span></td>
								<td class="number-cell">32,972.0<br><span style="margin-left: 1.5em; color: #555;"><em>54.1%</em></span></td>
								<td class="number-cell">81,453.0<br><span style="margin-left: 1.5em; color: #555;"><em>62.4%</em></span></td>
								<td class="number-cell">123,858.63<br><span style="margin-left: 1.5em; color: #555;"><em>61.99%</em></span></td>
							</tr>
							<tr>
								<td class="label-column">Earnings from Cont. Ops.<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em;">Margin %</span></td>
								<td class="number-cell">2,796.0<br><span style="margin-left: 1.5em; color: #555;"><em>25.6%</em></span></td>
								<td class="number-cell">4,332.0<br><span style="margin-left: 1.5em; color: #555;"><em>26.0%</em></span></td>
								<td class="number-cell">9,752.0<br><span style="margin-left: 1.5em; color: #555;"><em>36.2%</em></span></td>
								<td class="number-cell">4,368.0<br><span style="margin-left: 1.5em; color: #555;"><em>16.2%</em></span></td>
								<td class="number-cell">29,760.0<br><span style="margin-left: 1.5em; color: #555;"><em>48.8%</em></span></td>
								<td class="number-cell">72,880.0<br><span style="margin-left: 1.5em; color: #555;"><em>55.8%</em></span></td>
								<td class="number-cell">-<br><span style="margin-left: 1.5em; color: #555;"></span></td>
							</tr>
							<tr>
								<td class="label-column">Net Income<br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em;">Margin %</span></td>
								<td class="number-cell">2,796.0<br><span style="margin-left: 1.5em; color: #555;"><em>25.6%</em></span></td>
								<td class="number-cell">4,332.0<br><span style="margin-left: 1.5em; color: #555;"><em>26.0%</em></span></td>
								<td class="number-cell">9,752.0<br><span style="margin-left: 1.5em; color: #555;"><em>36.2%</em></span></td>
								<td class="number-cell">4,368.0<br><span style="margin-left: 1.5em; color: #555;"><em>16.2%</em></span></td>
								<td class="number-cell">29,760.0<br><span style="margin-left: 1.5em; color: #555;"><em>48.8%</em></span></td>
								<td class="number-cell">72,880.0<br><span style="margin-left: 1.5em; color: #555;"><em>55.8%</em></span></td>
								<td class="number-cell">105,175.35<br><span style="margin-left: 1.5em; color: #555;"><em>52.64%</em></span></td>
							</tr>
							<tr>
								<td class="label-column">Diluted EPS Excl. Extra Items<sup>3</sup><br><span style="margin-left: 1.5em; color: #555; font-size: 0.95em;">Growth Over Prior Year</span></td>
								<td class="number-cell">0.11<br><span style="margin-left: 1.5em; color: #555;"><em>(31.8%)</em></span></td>
								<td class="number-cell">0.17<br><span style="margin-left: 1.5em; color: #555;"><em>53.1%</em></span></td>
								<td class="number-cell">0.39<br><span style="margin-left: 1.5em; color: #555;"><em>122.5%</em></span></td>
								<td class="number-cell">0.17<br><span style="margin-left: 1.5em; color: #555;"><em>(55.8%)</em></span></td>
								<td class="number-cell">1.19<br><span style="margin-left: 1.5em; color: #555;"><em>600.0%</em></span></td>
								<td class="number-cell">2.94<br><span style="margin-left: 1.5em; color: #555;"><em>147.1%</em></span></td>
								<td class="number-cell">4.29<br><span style="margin-left: 1.5em; color: #555;"></span></td>
							</tr>
						</tbody>
					</table>
				</div>

				<div class="footnote">
					<div><sup>1</sup>All results are taken from the most recently filed statement for each period. When there has been more than one, earlier filings can be viewed on the individual statement pages.</div>
					<div><sup>3</sup>All forward period figures are consensus mean estimates provided by the brokers and may not be on a comparable basis as financials.</div>
					<div><sup>†</sup>Growth rates for forward periods are calculated against prior period estimates or actual pro forma results as disclosed on the Estimates Consensus page.</div>
				</div>

				<div class="capitalization-section">
					<div class="capitalization-header">
						Current Capitalization (Millions of USD)
					</div>
					<div class="cap-container">
						<div class="cap-table-col">
							<table style="width:100%; border-collapse:collapse; font-size:12px; font-family:'Times New Roman', Times, serif; color:#444950;">
								<tr style="font-style:italic; font-weight:bold; color:#444950; background:#f4f4f4;"><td style="font-style:italic; font-weight:bold; color:#444950; padding:2px 24px 2px 4px;">Currency</td><td style="text-align:right; font-style:italic; font-weight:bold; color:#444950; padding:2px 4px 2px 12px;">USD</td></tr>
								<tr><td style="color:#444950; padding:2px 24px 2px 4px;">Share Price</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">$ 157.25</td></tr>
								<tr style="background:#f4f4f4;"><td style="color:#444950; padding:2px 24px 2px 4px;">Shares Out.</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">24,400.0</td></tr>
								<tr style="border-top:2px solid #444950;"><td style="font-weight:bold; color:#222; padding-top:8px; padding:2px 24px 2px 4px;">Market Capitalization</td><td style="text-align:right; font-weight:bold; color:#0056d6; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">3,836,900.0</td></tr>
								<tr><td style="color:#444950; padding:2px 24px 2px 4px;">- Cash &amp; Short Term Investments</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">53,691.0</td></tr>
								<tr style="background:#f4f4f4;"><td style="color:#444950; padding:2px 24px 2px 4px;">+ Total Debt</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">10,285.0</td></tr>
								<tr><td style="color:#444950; padding:2px 24px 2px 4px;">+ Pref. Equity</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">-</td></tr>
								<tr style="background:#f4f4f4;"><td style="color:#444950; padding:2px 24px 2px 4px;">+ Total Minority Interest</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">-</td></tr>
								<tr><td style="color:#444950; padding:2px 24px 2px 4px;">- Long Term Marketable Securities</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">-</td></tr>
								<tr style="border-top:2px solid #444950; background:#f4f4f4;"><td style="font-weight:bold; color:#222; padding-top:8px; padding:2px 24px 2px 4px;">= Total Enterprise Value (TEV)</td><td style="text-align:right; font-weight:bold; color:#0056d6; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">3,793,494.0</td></tr>
								<tr><td style="color:#444950; padding:2px 24px 2px 4px;">Book Value of Common Equity</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">83,843.0</td></tr>
								<tr style="background:#f4f4f4;"><td style="color:#444950; padding:2px 24px 2px 4px;">+ Pref. Equity</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">-</td></tr>
								<tr><td style="color:#444950; padding:2px 24px 2px 4px;">+ Total Minority Interest</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">-</td></tr>
								<tr style="background:#f4f4f4;"><td style="color:#444950; padding:2px 24px 2px 4px;">+ Total Debt</td><td style="text-align:right; color:#222; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">10,285.0</td></tr>
								<tr style="border-top:2px solid #444950;"><td style="font-weight:bold; color:#222; padding-top:8px; padding:2px 24px 2px 4px;">= Total Capital</td><td style="text-align:right; font-weight:bold; color:#0056d6; font-family:'Times New Roman', Times, serif; padding:2px 4px 2px 12px;">94,128.0</td></tr>
							</table>
							<div class="footnote" style="margin-top:8px;">
								<div>**For companies that have multiple share classes that publicly trade, we are incorporating the different prices to calculate our company level market capitalization. Please click on the value to see the detailed calculation. Prices shown on this page are the close price of the company's primary stock class. Shares shown on this page are total company as-reported share values.</div>
							</div>
						</div>
						<div class="cap-chart-col">
							<img src="../img/cap-chart.png" alt="Capitalization Chart" onerror="this.src='../img/placeholder.png'">
							<div class="chart-legend">
								<div class="legend-items">
									<span class="legend-item"><span class="color-box net-liability"></span>Net Liability</span>
									<span class="legend-item"><span class="color-box total-liability"></span>Total Liability</span>
									<span class="legend-item"><span class="color-box common-equity"></span>Common Equity</span>
									<span class="legend-item"><span class="color-box market-cap"></span>Market Capitalization</span>
								</div>
								<div style="font-size:12px; color:#444; margin-top:4px;">Note: Striped area represents the impact of negative Net Liability on Market Cap.<br>
									Total Liability includes Total Debt, Minority Interest and Pref. Equity.<br>
									Net Liability includes Total Liability, net of Cash and Short Term Investments.<br>
									TEV includes Market Cap and Net Liability.<br>
									Total Capital includes Common Equity and Total Liability.
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="valuation-section" style="margin-bottom:32px;">
					<div class="valuation-table-container" style="width:100%; margin-top:8px;">
						<div class="capitalization-header" style="margin-bottom:8px;">
							Valuation Multiples based on Current Capitalization
						</div>
						<table class="valuation-multiples-table" style="width:100%; border-collapse:collapse; font-family:'Times New Roman', Times, serif; font-size:12px; background:#fff; color:#333; border:1px solid #999;">
							<thead>
								<tr style="background:#e8e8e8; border-bottom:1px solid #999;">
									<th style="padding:8px 12px; font-weight:bold; color:#333; font-size:12px; text-align:left; border-right:1px solid #999;">For the Fiscal Period Ending</th>
									<th style="text-align:center; padding:8px 10px; font-size:11px; font-weight:bold; color:#333; border-right:1px solid #999; line-height:1.2;">12 months<br>Jan-26-2025A</th>
									<th style="text-align:center; padding:8px 10px; font-size:11px; font-weight:bold; color:#333; border-right:1px solid #999; line-height:1.2;">LTM<br>March 2025</th>
									<th style="text-align:center; padding:8px 10px; font-size:11px; font-weight:bold; color:#333; border-right:1px solid #999; line-height:1.2;">12 months<br>Jan-31-2026E</th>
									<th style="text-align:center; padding:8px 10px; font-size:11px; font-weight:bold; color:#333; border-right:1px solid #999; line-height:1.2;">12 months<br>Jan-31-2027E</th>
									<th style="text-align:center; padding:8px 10px; font-size:11px; font-weight:bold; color:#333; line-height:1.2;">12 months<br>Jan-31-2028E</th>
								</tr>
							</thead>
							<tbody>
								<tr style="border-bottom:1px solid #ddd;">
									<td style="padding:6px 12px; font-weight:bold; color:#333; border-right:1px solid #999;">TEV/Total Revenue</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">29.1x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">25.5x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">19.24x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">15.33x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px;">13.21x</td>
								</tr>
								<tr style="border-bottom:1px solid #ddd;">
									<td style="padding:6px 12px; font-weight:bold; color:#333; border-right:1px solid #999;">TEV/EBITDA</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">45.5x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">42.8x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">32.52x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">25.72x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px;">22.27x</td>
								</tr>
								<tr style="border-bottom:1px solid #ddd;">
									<td style="padding:6px 12px; font-weight:bold; color:#333; border-right:1px solid #999;">TEV/EBIT</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">46.7x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">44.0x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">31.04x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">23.33x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px;">20.35x</td>
								</tr>
								<tr style="border-bottom:1px solid #ddd;">
									<td style="padding:6px 12px; font-weight:bold; color:#333; border-right:1px solid #999;">P/Diluted EPS Before Extra</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">53.5x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">50.7x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">37.13x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">27.64x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px;">23.97x</td>
								</tr>
								<tr style="border-bottom:1px solid #ddd;">
									<td style="padding:6px 12px; font-weight:bold; color:#333; border-right:1px solid #999;">P/BV</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">48.5x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">45.7x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">26.50x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">16.02x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px;">10.63x</td>
								</tr>
								<tr>
									<td style="padding:6px 12px; font-weight:bold; color:#333; border-right:1px solid #999;">Price/Tang BV</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">52.5x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">49.4x</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">-</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px; border-right:1px solid #999;">-</td>
									<td style="color:#0056d6; text-align:center; font-weight:normal; padding:6px 10px;">-</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		`;
	}

	/**
	 * Lifecycle hook - called after view is mounted
	 */
	protected onMount(): void {
		this.setupPdfButtons();
		this.initializeDownloadButton();
		this.initializeTimeline();
		this.triggerContentUpdate();
	}

	/**
	 * Initialize timeline selector
	 */
	private initializeTimeline(): void {
		// Use setTimeout to ensure DOM is fully rendered
		setTimeout(() => {
			const timelineSelector = window.__timelineSelector;
			if (timelineSelector !== null && timelineSelector !== undefined) {
				timelineSelector.initialize();
			} else {
				console.warn('[FinancialsView] TimelineSelector not available');
			}
		}, 0);
	}

	/**
	 * Trigger ContentManager to update content based on current state
	 * Explicitly pass 'key-stats' to avoid race condition with router pathname updates
	 */
	private triggerContentUpdate(): void {
		const contentManager = window.__contentManager;
		if (contentManager !== null && contentManager !== undefined) {
			contentManager.updateContent(undefined, 'key-stats');
		} else {
			console.warn('[FinancialsView] ContentManager not available');
		}
	}

	/**
	 * Set up PDF button click handlers
	 */
	private setupPdfButtons(): void {
		const pdfButtons = this.querySelectorAll<HTMLElement>('.pdf-button');

		pdfButtons.forEach((button) => {
			this.addEventListener(button, 'click', () => {
				const pdfPath = button.getAttribute('data-pdf');
				if (pdfPath) {
					window.open(pdfPath, '_blank');
				}
			});
		});
	}

	/**
	 * Set up event listener for download button
	 */
	private initializeDownloadButton(): void {
		const downloadButton = this.container?.querySelector('.download-button');

		if (!downloadButton) {
			console.warn('[FinancialsView] Download button not found in DOM');
			return;
		}

		this.addEventListener(downloadButton as HTMLElement, 'click', () => {
			this.handleDownloadClick();
		});
	}

	/**
	 * Handle download button click
	 * Downloads file based on current timeline range and period type
	 */
	private handleDownloadClick(): void {
		// Get current timeline range from storage
		const timelineRange = this.storageService.getTimelineRange();
		if (!timelineRange) {
			console.warn('Timeline range not available');
			return;
		}

		// Get current period type from storage
		const periodType = this.storageService.getPeriodType();

		// Trigger download using DownloadService with timeline and period criteria
		this.downloadService.downloadFinancialStatement({
			leftYear: timelineRange.leftYear,
			rightYear: timelineRange.rightYear,
			periodType
		});
	}
}
